/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable max-params */
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CustomSnackbarComponent } from '@shared/components/custom-snackbar/custom-snackbar.component';
import { TableConfirmationDialogComponent } from '@shared/components/wcg-table/table-actions/table-confirmation-dialog/table-confirmation-dialog.component';

import { OperationStatus } from '../types';
import { USER_DOWNLOAD_ACTIONS, USER_EXPORT_ACTIONS } from '../actions';
import { DownloadsExportsService } from '../services';
import { DOCUMENTS_DOWNLOAD_ROUTE, DOCUMENTS_EXPORT_ROUTE } from '../constants';

@Injectable()
export class DownloadExportsEffects {
  private readonly _router = inject(Router);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(MatDialog);
  private readonly _downloadExportService = inject(DownloadsExportsService);
  private readonly _actions$ = inject(Actions);

  /**
   * @USER DOWNLOADS EXPORTS EFFECTS
   * */
  loadDocumentDownload$ = createEffect(() =>
    this._actions$.pipe(
      ofType(USER_DOWNLOAD_ACTIONS.loadDocumentDownloads),
      switchMap(({ params }) =>
        this._downloadExportService.getDocumentDownloads(params).pipe(
          switchMap((downloadsData) => [
            USER_DOWNLOAD_ACTIONS.onLoadDocumentDownloadsSuccess({
              downloadsData,
            }),
          ]),
        ),
      ),
    ),
  );

  initiateDownload$ = createEffect(() =>
    this._actions$.pipe(
      ofType(USER_DOWNLOAD_ACTIONS.initiateDownload),
      switchMap(({ downloadRequest, data }) =>
        this._downloadExportService
          .downloadDocumentBundle(downloadRequest)
          .pipe(
            switchMap(() => [
              USER_DOWNLOAD_ACTIONS.downloadStarted({
                status: 'in-progress',
              }),
              USER_DOWNLOAD_ACTIONS.openDownloadConfirmationDialog({
                component: () => TableConfirmationDialogComponent,
                data,
              }),
            ]),
            catchError((error) =>
              of(
                USER_DOWNLOAD_ACTIONS.downloadFailure(
                  this._createFailurePayload(
                    error.message ?? 'Download failed',
                  ),
                ),
              ),
            ),
          ),
      ),
    ),
  );

  openDownloadDialog$ = createEffect(() =>
    this._actions$.pipe(
      ofType(USER_DOWNLOAD_ACTIONS.openDownloadConfirmationDialog),
      switchMap(({ component, data }) => {
        const _dialogRef = this._dialog.open(component(), {
          data: data,
        });
        return _dialogRef
          .afterClosed()
          .pipe(
            map((result) =>
              result
                ? USER_DOWNLOAD_ACTIONS.downloadDialogConfirmed()
                : USER_DOWNLOAD_ACTIONS.downloadDialogDismissed(),
            ),
          );
      }),
    ),
  );

  navigateDownloadOnConfirm$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(USER_DOWNLOAD_ACTIONS.downloadDialogConfirmed),
        tap(() => {
          // Close any existing dialogs before navigation
          this._dialog.closeAll();
          this.navigateToDownloadExportPage(DOCUMENTS_DOWNLOAD_ROUTE);
        }),
      ),
    { dispatch: false },
  );

  triggerDownloadAlert$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          USER_DOWNLOAD_ACTIONS.downloadSuccess,
          USER_DOWNLOAD_ACTIONS.downloadFailure,
        ),
        tap(({ fileName, fileStage }) => {
          const message = `The following export request ${fileStage ?? 'failed'}:\n\n${fileName}`;

          this.showSnackBarResponse(
            message,
            'Go to Downloads',
            DOCUMENTS_DOWNLOAD_ROUTE,
          );
        }),
      ),
    { dispatch: false },
  );

  getDocumentLink$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(USER_DOWNLOAD_ACTIONS.getDocumentLink),
        switchMap(({ documentId }) =>
          this._downloadExportService.getDocumentLink(documentId).pipe(
            tap((documentLink) => {
              window.open(documentLink.link, '_blank');
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  /**
   * @USER DOWNLOAD EFFECTS END
   * */

  /**
   * @USER EXPORTS EFFECTS
   * */

  loadDocumentExport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(USER_EXPORT_ACTIONS.loadDocumentExports),
      switchMap(({ params }) =>
        this._downloadExportService.getDocumentExports(params).pipe(
          switchMap((exportsData) => {
            return [
              USER_EXPORT_ACTIONS.onLoadDocumentExportsSuccess({
                exportsData,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  initiateExport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(USER_EXPORT_ACTIONS.initiateExport),
      switchMap(({ exportRequest, data }) =>
        this._downloadExportService.exportDocumentBundle(exportRequest).pipe(
          switchMap(() => [
            USER_EXPORT_ACTIONS.exportStarted({
              status: 'in-progress',
            }),
            USER_EXPORT_ACTIONS.openExportConfirmationDialog({
              component: () => TableConfirmationDialogComponent,
              data,
            }),
          ]),
          catchError((error) =>
            of(
              USER_EXPORT_ACTIONS.exportFailure(
                this._createFailurePayload(error.message ?? 'Export failed'),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  openExportDialog$ = createEffect(() =>
    this._actions$.pipe(
      ofType(USER_EXPORT_ACTIONS.openExportConfirmationDialog),
      switchMap(({ component, data }) => {
        const _dialogRef = this._dialog.open(component(), {
          data: data,
        });
        return _dialogRef
          .afterClosed()
          .pipe(
            map((result) =>
              result
                ? USER_EXPORT_ACTIONS.exportDialogConfirmed()
                : USER_EXPORT_ACTIONS.exportDialogDismissed(),
            ),
          );
      }),
    ),
  );

  navigateExportOnConfirm$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(USER_EXPORT_ACTIONS.exportDialogConfirmed),
        tap(() => {
          // Close any existing dialogs before navigation
          this._dialog.closeAll();
          this.navigateToDownloadExportPage(DOCUMENTS_EXPORT_ROUTE);
        }),
      ),
    { dispatch: false },
  );

  triggerExportAlert$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          USER_EXPORT_ACTIONS.exportSuccess,
          USER_EXPORT_ACTIONS.exportFailure,
        ),
        tap(({ fileName, fileStage }) => {
          const message = `The following export request ${fileStage ?? 'failed'}:\n\n${fileName}`;

          this.showSnackBarResponse(
            message,
            'Go to Exports',
            DOCUMENTS_EXPORT_ROUTE,
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * @USER EXPORTS EFFECTS END
   * */

  private _createFailurePayload(
    errorMessage: string,
    fileName = '',
  ): {
    errorMessage: string;
    fileName: string;
    status: OperationStatus;
  } {
    return {
      errorMessage,
      fileName,
      status: 'failure' as OperationStatus,
    };
  }

  private showSnackBarResponse(
    message: string,
    btnTitle: string,
    navigateTo: string,
  ) {
    this._snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        btnTitle,
        message,
        navigateTo,
      },
      horizontalPosition: 'center',
      panelClass: ['no-default-padding'],
      verticalPosition: 'bottom',
    });
  }

  private navigateToDownloadExportPage(navigateTo: string) {
    this._router.navigate([navigateTo]);
  }
}
